/* global Swiper */
(() => {
  const defineCollectionSwiper = () => {
    if (customElements.get('collection-swiper')) return;

    class CollectionSwiper extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('.swiper');
        this.wrapperEl = this.querySelector('.swiper-wrapper');
        this.slides = Array.from(this.querySelectorAll('.swiper-slide'));
        this.navPrev = this.querySelector('.slider-nav__btn[name=prev]');
        this.navNext = this.querySelector('.slider-nav__btn[name=next]');

        if (!this.swiperEl || !this.wrapperEl) return;

        this.slidesCount = parseInt(this.dataset.slidesCount || '0', 10);
        this.desktopPerView = parseInt(this.dataset.desktopSlidesPerView || '5', 10);
        this.breakpoint = parseInt(this.dataset.breakpoint || '750', 10);
        this.enableDesktopCarousel = this.dataset.enableDesktopCarousel === 'true';

        // 1) Prova Swiper se disponibile
        if (typeof window.Swiper !== 'undefined') {
          this.initSwiper();
        } else {
          // 2) Fallback immediato (frecce + swipe/scroll nativo)
          this.initFallback();

          // 3) Se il tema carica Swiper dopo (evento), “upgrade” automatico
          this._onSwiperLoaded = () => {
            if (this.swiper) return;
            if (typeof window.Swiper === 'undefined') return;
            this.destroyFallback();
            this.initSwiper();
            this.updateNavVisibility();
          };
          document.addEventListener('swiper-loaded', this._onSwiperLoaded, { once: true });
        }

        this.updateNavVisibility();

        this._onResize = () => {
          // se siamo in fallback, riallineo le dimensioni al resize
          if (!this.swiper) this.applyFallbackSizing();
          this.updateNavVisibility();
        };
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      disconnectedCallback() {
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._onSwiperLoaded) document.removeEventListener('swiper-loaded', this._onSwiperLoaded);
        if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
      }

      /* -------------------- SWIPER MODE -------------------- */
      initSwiper() {
        if (this.slidesCount <= 1) return;

        const options = {
          grabCursor: true,
          centeredSlides: false,
          loop: false,
          watchOverflow: true,
          slidesPerView: 1.2,
          spaceBetween: 16,
          navigation: {
            nextEl: `#${CSS.escape(this.id)} .slider-nav__btn[name=next]`,
            prevEl: `#${CSS.escape(this.id)} .slider-nav__btn[name=prev]`
          },
          breakpoints: {}
        };

        options.breakpoints[this.breakpoint] = {
          slidesPerView: this.desktopPerView,
          spaceBetween: 24,
          // Desktop: swipe solo se >5 (come tua regola)
          allowTouchMove: this.enableDesktopCarousel,
          grabCursor: this.enableDesktopCarousel
        };

        this.swiper = new Swiper(`#${CSS.escape(this.id)} .swiper`, options);
      }

      /* -------------------- FALLBACK MODE -------------------- */
      initFallback() {
        // forza scroll orizzontale anche se il tema imposta overflow hidden
        this.swiperEl.style.setProperty('overflow-x', 'auto', 'important');
        this.swiperEl.style.setProperty('overflow-y', 'hidden', 'important');
        this.swiperEl.style.setProperty('-webkit-overflow-scrolling', 'touch');

        // wrapper in riga
        this.wrapperEl.style.display = 'flex';
        this.wrapperEl.style.padding = '0';
        this.wrapperEl.style.margin = '0';

        // sizing coerente con le CSS variables del Liquid
        this.applyFallbackSizing();

        // frecce: scrollBy nativo
        this._onPrev = (e) => {
          e.preventDefault();
          this.swiperEl.scrollBy({ left: -(this.swiperEl.clientWidth * 0.9), behavior: 'smooth' });
        };
        this._onNext = (e) => {
          e.preventDefault();
          this.swiperEl.scrollBy({ left: this.swiperEl.clientWidth * 0.9, behavior: 'smooth' });
        };

        if (this.navPrev) this.navPrev.addEventListener('click', this._onPrev);
        if (this.navNext) this.navNext.addEventListener('click', this._onNext);
      }

      applyFallbackSizing() {
        const styles = getComputedStyle(this);
        const isDesktop = window.innerWidth >= this.breakpoint;

        const gapMobile = parseFloat(styles.getPropertyValue('--cc-gap-mobile')) || 16;
        const gapDesktop = parseFloat(styles.getPropertyValue('--cc-gap-desktop')) || 24;
        const mobileSlide = (styles.getPropertyValue('--cc-mobile-slide') || '82%').trim();
        const desktopSlides = parseInt(styles.getPropertyValue('--cc-desktop-slides') || `${this.desktopPerView}`, 10) || this.desktopPerView;

        const gap = isDesktop ? gapDesktop : gapMobile;
        this.wrapperEl.style.gap = `${gap}px`;

        this.slides.forEach((slide) => {
          slide.style.flex = '0 0 auto';
          if (isDesktop) {
            slide.style.width = `calc((100% - (${desktopSlides} - 1) * ${gap}px) / ${desktopSlides})`;
          } else {
            slide.style.width = mobileSlide;
          }
        });
      }

      destroyFallback() {
        // rimuovo listener bottoni
        if (this.navPrev && this._onPrev) this.navPrev.removeEventListener('click', this._onPrev);
        if (this.navNext && this._onNext) this.navNext.removeEventListener('click', this._onNext);

        // pulizia inline styles principali (swiper si occuperà del layout)
        this.swiperEl.style.removeProperty('overflow-x');
        this.swiperEl.style.removeProperty('overflow-y');

        this.wrapperEl.style.removeProperty('display');
        this.wrapperEl.style.removeProperty('gap');

        this.slides.forEach((slide) => {
          slide.style.removeProperty('flex');
          slide.style.removeProperty('width');
        });
      }

      /* -------------------- NAV VISIBILITY -------------------- */
      updateNavVisibility() {
        const nav = this.querySelector('.slider-nav');
        if (!nav) return;

        const isDesktop = window.innerWidth >= this.breakpoint;

        // Mobile: nav se più di 1
        // Desktop: nav SOLO se >5 (enableDesktopCarousel true)
        const shouldShow = isDesktop ? this.enableDesktopCarousel : this.slidesCount > 1;

        nav.style.display = shouldShow ? '' : 'none';
      }
    }

    customElements.define('collection-swiper', CollectionSwiper);
  };

  // Definisci il custom element subito
  defineCollectionSwiper();
})();